import {
  DEFAULT_SCHEDULE_INTERVALS,
  StatusType,
} from '@spotify/confidence-core-react';
import {
  MaterializedSegmentLoadJobFragment,
  MaterializerV1MaterializedSegmentLoadJobJobState,
  MaterializerV1ScheduledMaterializedSegmentLoadJobState,
  ScheduledMaterializedSegmentLoadJobFragment,
} from '@spotify/confidence-graphql';

export const isScheduledLoadJob = (
  job: MaterializedSegmentLoadJobFragment,
): boolean =>
  job.schedule?.__typename ===
  'MaterializerV1ScheduledMaterializedSegmentLoadJob';

export const formatDuration = (ms: number): string => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
};

export const formatFalsePositiveRate = (rate: number): string => {
  if (rate <= 0) return '0% of';
  if (rate >= 0.01) return `${(rate * 100).toFixed(1)}% of`;
  const ratio = Math.round(1 / rate);
  return `1 in ${ratio.toLocaleString()}`;
};

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
};

export const getTotalDuration = (
  loadJob: MaterializedSegmentLoadJobFragment,
): number | null => {
  const createTime = new Date(loadJob.createTime).getTime();
  const bloomFilterEndTime = loadJob.bloomFilterStatus?.bloomFilterEndTime
    ? new Date(loadJob.bloomFilterStatus.bloomFilterEndTime).getTime()
    : null;
  const ingestionEndTime = loadJob.ingestionStatus?.ingestionEndTime
    ? new Date(loadJob.ingestionStatus.ingestionEndTime).getTime()
    : null;
  const exportEndTime = loadJob.exportEndTime
    ? new Date(loadJob.exportEndTime).getTime()
    : null;

  if (bloomFilterEndTime) {
    return bloomFilterEndTime - createTime;
  }
  if (ingestionEndTime) {
    return ingestionEndTime - createTime;
  }
  if (exportEndTime) {
    return exportEndTime - createTime;
  }
  return null;
};

export const getScheduleName = (
  job: MaterializedSegmentLoadJobFragment,
): string | undefined => {
  if (
    job.schedule?.__typename ===
    'MaterializerV1ScheduledMaterializedSegmentLoadJob'
  ) {
    return job.schedule.name;
  }
  return undefined;
};

const LIVE_STATES = [
  MaterializerV1ScheduledMaterializedSegmentLoadJobState.Active,
  MaterializerV1ScheduledMaterializedSegmentLoadJobState.Paused,
];

export const isLiveSchedule = (
  schedule: Pick<ScheduledMaterializedSegmentLoadJobFragment, 'state'>,
): boolean => Boolean(schedule.state && LIVE_STATES.includes(schedule.state));

export const scheduleStateToStatusType: Record<
  MaterializerV1ScheduledMaterializedSegmentLoadJobState,
  StatusType
> = {
  [MaterializerV1ScheduledMaterializedSegmentLoadJobState.Active]:
    StatusType.OK,
  [MaterializerV1ScheduledMaterializedSegmentLoadJobState.Paused]:
    StatusType.WARNING,
  [MaterializerV1ScheduledMaterializedSegmentLoadJobState.Cancelled]:
    StatusType.ABORTED,
  [MaterializerV1ScheduledMaterializedSegmentLoadJobState.Completed]:
    StatusType.OK,
};

export const scheduleStateLabel: Record<
  MaterializerV1ScheduledMaterializedSegmentLoadJobState,
  string
> = {
  [MaterializerV1ScheduledMaterializedSegmentLoadJobState.Active]: 'Active',
  [MaterializerV1ScheduledMaterializedSegmentLoadJobState.Paused]: 'Paused',
  [MaterializerV1ScheduledMaterializedSegmentLoadJobState.Cancelled]:
    'Cancelled',
  [MaterializerV1ScheduledMaterializedSegmentLoadJobState.Completed]:
    'Completed',
};

export type LoadJobStateInfo = {
  statusType: StatusType;
  label: string;
};

const loadJobStateInfo: Record<
  MaterializerV1MaterializedSegmentLoadJobJobState,
  LoadJobStateInfo
> = {
  [MaterializerV1MaterializedSegmentLoadJobJobState.JobStateCompleted]: {
    statusType: StatusType.OK,
    label: 'Completed',
  },
  [MaterializerV1MaterializedSegmentLoadJobJobState.JobStateFailed]: {
    statusType: StatusType.ERROR,
    label: 'Failed',
  },
  [MaterializerV1MaterializedSegmentLoadJobJobState.JobStateRunning]: {
    statusType: StatusType.RUNNING,
    label: 'Running',
  },
  [MaterializerV1MaterializedSegmentLoadJobJobState.JobStatePending]: {
    statusType: StatusType.PENDING,
    label: 'Pending',
  },
  [MaterializerV1MaterializedSegmentLoadJobJobState.JobStateCancelled]: {
    statusType: StatusType.WARNING,
    label: 'Cancelled',
  },
};

const isLoadJobState = (
  state: string,
): state is MaterializerV1MaterializedSegmentLoadJobJobState =>
  state in loadJobStateInfo;

export const getLoadJobStateInfo = (
  state?: string | null,
): LoadJobStateInfo => {
  if (state && isLoadJobState(state)) {
    return loadJobStateInfo[state];
  }
  return { statusType: StatusType.WARNING, label: state || 'Unknown' };
};

export const formatInterval = (intervalStr: string): string => {
  const seconds = parseInt(intervalStr.replace('s', ''), 10);
  if (isNaN(seconds) || seconds <= 0) return intervalStr;
  const knownInterval = DEFAULT_SCHEDULE_INTERVALS.find(
    interval => interval.durationMs === seconds * 1000,
  );
  if (knownInterval) return knownInterval.label;
  const hours = seconds / 3600;
  if (hours >= 24 && hours % 24 === 0) return `Every ${hours / 24}d`;
  if (hours >= 1) return `Every ${hours}h`;
  const minutes = seconds / 60;
  if (minutes >= 1) return `Every ${minutes}m`;
  return `Every ${seconds}s`;
};

export type LoadJobGroup =
  | {
      type: 'schedule';
      schedule: ScheduledMaterializedSegmentLoadJobFragment;
      loadJobs: MaterializedSegmentLoadJobFragment[];
    }
  | {
      type: 'manual';
      loadJob: MaterializedSegmentLoadJobFragment;
    };

const isLiveScheduleGroup = (group: LoadJobGroup): boolean =>
  group.type === 'schedule' && isLiveSchedule(group.schedule);

export const getLatestJobCreateTime = (
  loadJobs: MaterializedSegmentLoadJobFragment[],
): string | undefined =>
  loadJobs.reduce<string | undefined>(
    (latest, job) =>
      !latest || job.createTime > latest ? job.createTime : latest,
    undefined,
  );

const getLatestRunTime = (group: LoadJobGroup): string =>
  group.type === 'manual'
    ? group.loadJob.createTime
    : (getLatestJobCreateTime(group.loadJobs) ?? '');

export const groupLoadJobsBySchedule = (
  loadJobs: MaterializedSegmentLoadJobFragment[],
  schedules: ScheduledMaterializedSegmentLoadJobFragment[],
): LoadJobGroup[] => {
  const jobsBySchedule = new Map<
    string,
    MaterializedSegmentLoadJobFragment[]
  >();
  const groups: LoadJobGroup[] = [];

  for (const job of loadJobs) {
    const scheduleName = getScheduleName(job);
    if (scheduleName) {
      const existing = jobsBySchedule.get(scheduleName);
      if (existing) {
        existing.push(job);
      } else {
        jobsBySchedule.set(scheduleName, [job]);
      }
    } else {
      groups.push({ type: 'manual', loadJob: job });
    }
  }

  for (const schedule of schedules) {
    const scheduleJobs = jobsBySchedule.get(schedule.name);
    if (scheduleJobs) {
      groups.push({ type: 'schedule', schedule, loadJobs: scheduleJobs });
      jobsBySchedule.delete(schedule.name);
    }
  }

  for (const unmatchedScheduleJobs of jobsBySchedule.values()) {
    for (const loadJob of unmatchedScheduleJobs) {
      groups.push({ type: 'manual', loadJob });
    }
  }

  return groups.sort((a, b) => {
    const aLive = isLiveScheduleGroup(a);
    const bLive = isLiveScheduleGroup(b);
    if (aLive !== bLive) return aLive ? -1 : 1;
    return getLatestRunTime(b).localeCompare(getLatestRunTime(a));
  });
};
